import joi from 'joi';
import z from 'zod';
import { $uuid } from '../../utils/joi/schemas';

export const interactionType$ = z.enum(['STORE_RECEIPT']);
export type InteractionType = z.infer<typeof interactionType$>;

export enum ProfileInteractionStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Refused = 'Refused',
}

export interface InteractionAttributes {
  id: string;
  accountId: string;
  name: string;
  type: InteractionType;
  detailTemplate: string;
  contentExample: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export const $contentExample = z.record(z.unknown()).nullable();

export const $createInteractionBody = z.object({
  name: z.string().min(1),
  type: interactionType$,
  detailTemplate: z.string().min(1),
  contentExample: $contentExample.optional(),
});

export type CreateInteractionBody = z.infer<typeof $createInteractionBody>;

export const $updateInteractionBody = z.object({
  name: z.string().min(1),
  detailTemplate: z.string().min(1),
  contentExample: $contentExample.optional(),
});

export type UpdateInteractionBody = z.infer<typeof $updateInteractionBody>;

export interface ProfileInteractionAttributes {
  id: string;
  accountId: string;
  programId: string;
  profileId: string;
  interactionId: string;
  externalId: string;
  label: string;
  status: ProfileInteractionStatus;
  transactionId?: string;
  eventTimestamp: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileInteractionDetailsAttributes {
  id: string;
  profileInteractionId: string;
  status: ProfileInteractionStatus;
  eventTimestamp: string;
  eventId: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface ProfileInteractionDetailItem {
  id: string;
  status: ProfileInteractionStatus;
  eventTimestamp: string;
  eventId: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface ProfileInteractionPayload extends ProfileInteractionAttributes {
  detailTemplate: string;
  details: ProfileInteractionDetailItem[];
}

/** Shape for optional `impact.interaction` on apply-impact (Flow v3). */
export interface ImpactInteraction {
  programId: string;
  interactionId: string;
  externalId: string;
  label: string;
  status: ProfileInteractionStatus;
  data?: Record<string, unknown>;
  eventTimestamp: string;
}

export const $impactInteraction = joi.object<ImpactInteraction>({
  programId: $uuid,
  interactionId: $uuid,
  externalId: joi.string().required(),
  label: joi.string().required(),
  status: joi.string().required(),
  data: joi.object().unknown(true).optional(),
  eventTimestamp: joi.string().isoDate().required(),
});

export const $getProfileInteractionsQueryParams = z.object({
  limit: z.coerce.number().optional().default(10),
  skip: z.coerce.number().optional().default(0),
  programId: z.string().uuid().optional(),
  interactionIds: z.array(z.string().uuid()).optional(),
});

export type GetProfileInteractionsQueryParams = z.infer<typeof $getProfileInteractionsQueryParams>;
